import { useCallback, useState } from "react";

import { ArrowRightIcon } from "@vericus/cadmus-icons";
import {
  Alert,
  Button,
  Color,
  colors,
  Modal,
  RadioGroup,
  Text,
  TextArea,
} from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { __GLOBAL_SESSION_ID } from "@/client/globals";
import { SentimentCheckInput, SentimentCheckLevel } from "@/generated/graphql";

import * as styles from "./sentiment-check-modal.css";

type sentimentCheck = Omit<SentimentCheckInput, "sessionId" | "order">;
export interface Props {
  /** Callback function of submit button */
  // omit sessionId from the input
  onSubmit: (input: sentimentCheck) => Promise<void>;
  /** Whether students are allowed to skip the check. */
  skipEnabled?: boolean;
}

export const SentimentCheckModal = create(
  ({ onSubmit, skipEnabled }: Props) => {
    const modal = useModal();
    return (
      <SentimentCheckModalContent
        open={modal.visible}
        onExitComplete={modal.remove}
        onSubmit={onSubmit}
        skipEnabled={skipEnabled}
      />
    );
  }
);

export interface SentimentCheckModalProps {
  open: boolean;
  onExitComplete: VoidFunction;
  onSubmit?: (input: sentimentCheck) => Promise<void>;
  /** Whether students are allowed to skip the check. */
  skipEnabled?: boolean;
}

export function SentimentCheckModalContent(props: SentimentCheckModalProps) {
  const [comment, setComment] = useState<string>("");
  const [level, setLevel] = useState<SentimentCheckLevel | null>(null);
  const { onSubmit, onExitComplete, skipEnabled = true } = props;
  const onClick = useCallback(
    async (skipped: boolean) => {
      if (!onSubmit) return;
      // A non-skipped check requires a selected level: guard against
      // submitting an empty sentiment check even if the button is enabled.
      if (!skipped && level === null) return;
      await onSubmit({
        comment: !skipped ? comment : null,
        skipped: skipped,
        level: !skipped ? level : null,
      });
      onExitComplete();
    },
    [onSubmit, comment, level, onExitComplete]
  );

  const buttonLabels = [
    {
      labelCode: SentimentCheckLevel.Green,
      label: "Group is working well together",
      bgColor: colors.successBg,
      indicatorColor: "functionalSuccess" as Color,
    },
    {
      labelCode: SentimentCheckLevel.Orange,
      label: "Some challenges, but manageable",
      bgColor: colors.functionalAlertBg,
      indicatorColor: "functionalAlert" as Color,
    },
    {
      labelCode: SentimentCheckLevel.Red,
      label: "Significant issues impacting progress",
      bgColor: colors.functionalFailBg,
      indicatorColor: "functionalFail" as Color,
    },
  ];
  return (
    <Modal.Root open={props.open}>
      <Modal.Content
        width={834}
        className={styles.content}
        withCloseButton={false}
      >
        <Modal.Title asChild>
          <Text kind="headingThree" textAlign="center" style={{ padding: 0 }}>
            Sentiment Check: How is Your Group Doing?
          </Text>
        </Modal.Title>
        <Modal.Description asChild>
          <Text kind="bodyLine" textAlign="center">
            Please rate your group experience so far. This helps us understand
            how your group is progressing and identify any areas where support
            may be needed.
          </Text>
        </Modal.Description>
        <Alert severity="info" size="sm">
          Your sentiment check feedback is seen by your teacher but stays hidden
          from your fellow group members.
        </Alert>

        <RadioGroup.Root
          onValueChange={(value) => {
            setLevel(value as SentimentCheckLevel);
          }}
          className={styles.radioGroup}
          value={level ?? undefined}
        >
          {buttonLabels.map((button) => (
            <RadioGroup.Card
              key={button.labelCode}
              value={button.labelCode}
              indicatorColor={button.indicatorColor}
              style={{
                backgroundColor: button.bgColor,
                borderColor:
                  level === button.labelCode
                    ? colors[button.indicatorColor]
                    : "transparent",
              }}
              className={styles.radioCard}
              description={
                <Text kind="bodyLine" color={"navy400"}>
                  {button.label}
                </Text>
              }
            />
          ))}
        </RadioGroup.Root>

        <div className={styles.commentContainer}>
          <Text kind="label" bold style={{ marginBottom: 10 }}>
            Comment (optional)
          </Text>
          <TextArea.Input
            id="sentiment-check-comment"
            placeholder="Enter your comment here..."
            minRows={3}
            value={comment}
            style={{ padding: "10px 16px", width: 746 }}
            maxLength={200}
            maxRows={10}
            onChange={(e) => {
              setComment(e.target.value);
            }}
          />
        </div>
        <div className={styles.footerButtonContainer}>
          {skipEnabled && (
            <Button kind="outline" onClick={() => onClick(true)}>
              Skip
            </Button>
          )}
          <Button
            kind="primary"
            disabled={level === null}
            rightIcon={
              <ArrowRightIcon
                label="Submit"
                size={12}
                style={{ marginLeft: "5px" }}
              />
            }
            onClick={() => onClick(false)}
          >
            Submit{" "}
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
}
